import { css } from '@emotion/core'
import { ArrowCaret, ContentAttached } from '@fs/zion-icon'
import { useHistory } from '@fs/zion-router'
import { Button, ResponsiveTableRow } from '@fs/zion-ui'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useNotifications from '../../../hooks/useNotifications'
import { getReservationSupplement } from '../../../services/reservationsService'
import { getBooleanDisplayValue, getEnumAbbreviation } from '../../../util/StringUtil'
import { OsidLink, UserLink } from '../../app/Links'
import GroupReservationPersons from './GroupReservationPersons'

const floatRight = css`
  float: right;
`
const hiddenDetailsLabel = css`
  font-size: 0.9rem;
`

export default function GroupReservationRow({ personData, reservation, userNames, ...otherProps }) {
  const { t } = useTranslation()
  const { error } = useNotifications()
  const [showHiddenDetails, setShowHiddenDetails] = useState(false)
  const [ranData, setRanData] = useState()
  const history = useHistory()

  useEffect(() => {
    let isCurrent = true
    if (!ranData) {
      getReservationSupplement(reservation.encodedRanId)
        .then((response) => {
          if (isCurrent) {
            setRanData(response.data)
          }
        })
        .catch((err) => {
          if (isCurrent) {
            error(
              t('groups.errors.gettingReservationSupplement', {
                defaultValue: `Error getting reservation supplement: ${err.message || ''}`,
                message: err.message || '',
              }),
              err
            )
          }
        })
    }
    return () => {
      isCurrent = false
    }
  }, [error, ranData, reservation.encodedRanId, t])

  const navigateToOsid = (osid) => {
    history.push(`/temple/ows/${osid}/summary?idType=osid`)
  }

  return (
    <ResponsiveTableRow
      {...otherProps}
      name={reservation.encodedRanId}
      column1={
        <GroupReservationPersons
          showHiddenDetails={showHiddenDetails}
          personIdGroup={reservation.personIdGroup}
          outerPersonIdGroup={reservation.outerPersonIdGroup}
          personData={personData}
        />
      }
      column2={getEnumAbbreviation(reservation.ordinanceType)}
      column3={<UserLink showId={false} userId={reservation.ownerId} userName={userNames[reservation.ownerId]} />}
      column4={
        <>
          {reservation.assignedTo}
          {showHiddenDetails && (
            <ReservationHiddenListModified
              onUserList={getBooleanDisplayValue(ranData?.onUserList)}
              lastModifiedDate={reservation.lastModifiedDate}
            />
          )}
        </>
      }
      column5={reservation.reservedDate}
      column6={
        <>
          {reservation.assignedDate}
          {showHiddenDetails && (
            <ReservationHiddenExpiryTypeOsid expiryType={reservation.expiryType} osid={reservation.osid} />
          )}
        </>
      }
      column7={reservation.printedDate ? `${reservation.printedDate}` : t('reservations.notPrinted', 'Not Printed')}
      column8={
        <>{reservation.expiryDate ? `${reservation.expiryDate}` : t('reservations.noExpiryDate', 'No Exp Date')}</>
      }
      column9={
        <>
          {reservation.encodedRanId}
          <span css={floatRight}>
            {reservation.osid && (
              <Button
                Icon={ContentAttached}
                onClick={() => navigateToOsid(reservation.osid)}
                description={reservation.osid}
              />
            )}
            <Button
              id="Show Hidden Details"
              Icon={ArrowCaret}
              iconDirection={showHiddenDetails ? 'up' : 'down'}
              onClick={() => setShowHiddenDetails(!showHiddenDetails)}
            />
          </span>
        </>
      }
    />
  )
}

const ReservationHiddenListModified = ({ onUserList, lastModifiedDate }) => {
  const { t } = useTranslation()
  return (
    <>
      <br />
      <br />
      <br />
      <div>
        <strong css={hiddenDetailsLabel}>{`${t('reservations.onUserList', 'On User List')}: `}</strong>
        {onUserList}
      </div>
      <br />
      <div>
        <strong css={hiddenDetailsLabel}>{`${t('res.lastModifiedDate', 'Last Modified Date')}: `}</strong>
        {lastModifiedDate}
      </div>
    </>
  )
}

const ReservationHiddenExpiryTypeOsid = ({ expiryType, osid }) => {
  const { t } = useTranslation()
  return (
    <>
      <br />
      <br />
      <br />
      <div>
        <strong css={hiddenDetailsLabel}>{`${t('res.expiryType', 'Expiry Type')}: `}</strong>
        {expiryType}
      </div>
      {osid && (
        <>
          <br />
          <div>
            <strong css={hiddenDetailsLabel}>{`${t('osid', 'OSID')}: `}</strong>
            <OsidLink osid={osid} />
          </div>
        </>
      )}
    </>
  )
}
